import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Plane, Building2, Car, MapPin, CalendarIcon, Users, Search, ArrowLeftRight, ChevronDown } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar } from './ui/calendar';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { airports } from '../mock/mock';
import { cn } from '../lib/utils';

const AirportPicker = ({ value, onChange, placeholder, icon: Icon, testId }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return airports.slice(0, 8);
    return airports.filter(a =>
      a.code.toLowerCase().includes(query) ||
      a.city.toLowerCase().includes(query) ||
      a.name.toLowerCase().includes(query)
    ).slice(0, 10);
  }, [q]);
  const selected = airports.find(a => a.code === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="w-full text-left group" data-testid={testId}>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{placeholder}</div>
          <div className="flex items-center gap-2 mt-1">
            <Icon className="w-4 h-4 text-teal-700 shrink-0" />
            <div className="font-display font-bold text-slate-900 truncate" data-testid={testId ? `${testId}-value` : undefined}>
              {selected ? `${selected.city} (${selected.code})` : 'Select airport'}
            </div>
          </div>
          <div className="text-xs text-slate-500 truncate">{selected?.name || 'Search cities or airports'}</div>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0">
        <div className="p-3 border-b">
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="City or airport code" data-testid={testId ? `${testId}-input` : undefined} />
        </div>
        <div className="max-h-72 overflow-y-auto py-2" data-testid={testId ? `${testId}-list` : undefined}>
          {filtered.map(a => (
            <button key={a.code} type="button"
              data-testid={testId ? `${testId}-opt-${a.code}` : undefined}
              onClick={() => { onChange(a.code); setOpen(false); setQ(''); }}
              className="w-full text-left px-3 py-2 hover:bg-teal-50 flex items-center gap-3">
              <div className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center text-[11px] font-bold text-slate-700">{a.code}</div>
              <div className="min-w-0">
                <div className="font-semibold text-sm text-slate-900 truncate">{a.city}</div>
                <div className="text-xs text-slate-500 truncate">{a.name}</div>
              </div>
            </button>
          ))}
          {filtered.length === 0 && <div className="px-4 py-6 text-sm text-slate-500">No matches</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const DateField = ({ label, date, onSelect, minDate, testId }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" className="w-full text-left" data-testid={testId}>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div className="flex items-center gap-2 mt-1">
          <CalendarIcon className="w-4 h-4 text-teal-700" />
          <div className="font-display font-bold text-slate-900">
            {date ? format(date, 'EEE, d MMM') : 'Add date'}
          </div>
        </div>
        <div className="text-xs text-slate-500">{date ? format(date, 'yyyy') : 'Choose your travel date'}</div>
      </button>
    </PopoverTrigger>
    <PopoverContent align="start" className="w-auto p-0">
      <Calendar
        mode="single"
        selected={date}
        onSelect={onSelect}
        disabled={(d) => (minDate ? d < minDate : d < new Date(new Date().setHours(0,0,0,0)))}
        initialFocus
      />
    </PopoverContent>
  </Popover>
);

const PassengerPicker = ({ value, onChange }) => {
  const { adults, children, infants, cabin } = value;
  const update = (k, v) => onChange({ ...value, [k]: v });
  const Row = ({ label, sub, k, min = 0 }) => (
    <div className="flex items-center justify-between py-2">
      <div>
        <div className="font-semibold text-sm text-slate-900">{label}</div>
        <div className="text-xs text-slate-500">{sub}</div>
      </div>
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => update(k, Math.max(min, value[k] - 1))} className="w-8 h-8 rounded-full border border-slate-300 hover:border-teal-600 text-slate-700 disabled:opacity-40" disabled={value[k] <= min}>−</button>
        <div className="w-6 text-center font-semibold">{value[k]}</div>
        <button type="button" onClick={() => update(k, value[k] + 1)} className="w-8 h-8 rounded-full border border-slate-300 hover:border-teal-600 text-slate-700">+</button>
      </div>
    </div>
  );
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="w-full text-left">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Travellers & cabin</div>
          <div className="flex items-center gap-2 mt-1">
            <Users className="w-4 h-4 text-teal-700" />
            <div className="font-display font-bold text-slate-900">{adults + children + infants} traveller{adults+children+infants!==1?'s':''}</div>
            <ChevronDown className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-xs text-slate-500">{cabin}</div>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <Row label="Adults" sub="12+ years" k="adults" min={1} />
        <Row label="Children" sub="2–11 years" k="children" />
        <Row label="Infants" sub="Under 2" k="infants" />
        <div className="mt-2">
          <Label className="text-xs uppercase tracking-widest text-slate-500">Cabin class</Label>
          <Select value={cabin} onValueChange={(v) => update('cabin', v)}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Economy">Economy</SelectItem>
              <SelectItem value="Premium Economy">Premium Economy</SelectItem>
              <SelectItem value="Business">Business</SelectItem>
              <SelectItem value="First">First</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const MAX_LEGS = 6;

const emptyLeg = (offsetDays = 7) => ({
  from: 'SIN',
  to: 'BKK',
  depart: new Date(Date.now() + offsetDays * 86400000),
});

const initialLegsFrom = (initial) => {
  if (initial?.legs && Array.isArray(initial.legs) && initial.legs.length) {
    return initial.legs.map(l => ({
      from: l.from || 'SIN',
      to: l.to || 'BKK',
      depart: l.depart ? new Date(l.depart) : new Date(Date.now() + 7 * 86400000),
    }));
  }
  const first = {
    from: initial?.from || 'SIN',
    to: initial?.to || 'BKK',
    depart: initial?.depart ? new Date(initial.depart) : new Date(Date.now() + 7 * 86400000),
  };
  if (initial?.tripType === 'multi') {
    return [first, { from: first.to, to: 'HKG', depart: new Date(first.depart.getTime() + 3 * 86400000) }];
  }
  return [first];
};

const FlightForm = ({ onSearch, initial }) => {
  const [tripType, setTripType] = useState(initial?.tripType || 'return');
  const [legs, setLegs] = useState(() => initialLegsFrom(initial));
  const [ret, setRet] = useState(initial?.ret ? new Date(initial.ret) : new Date(Date.now() + 14 * 86400000));
  const [pax, setPax] = useState(initial?.pax || { adults: 1, children: 0, infants: 0, cabin: 'Economy' });
  const [directOnly, setDirectOnly] = useState(!!initial?.directOnly);

  const updateLeg = (idx, patch) => setLegs(prev => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const swapLeg = (idx) => setLegs(prev => prev.map((l, i) => (i === idx ? { ...l, from: l.to, to: l.from } : l)));
  const addLeg = () => setLegs(prev => {
    if (prev.length >= MAX_LEGS) return prev;
    const last = prev[prev.length - 1];
    return [...prev, { from: last.to, to: 'HKG', depart: new Date(last.depart.getTime() + 3 * 86400000) }];
  });
  const removeLeg = (idx) => setLegs(prev => (prev.length <= 2 ? prev : prev.filter((_, i) => i !== idx)));

  // When switching modes, normalise legs immediately so no stale leg/date/ret
  // from the previous mode can survive into the next search.
  const changeTripType = (v) => {
    setTripType(v);
    setLegs(prev => {
      const first = prev[0] || emptyLeg();
      if (v === 'multi') {
        return [first, { from: first.to, to: 'HKG', depart: new Date(first.depart.getTime() + 3 * 86400000) }];
      }
      return [first]; // return + oneway both use single outbound in `legs`; ret is kept in its own state and only used for Return mode
    });
    if (v !== 'return') {
      // Blank out any lingering return date so a later switch back to Return
      // starts from a clean minDate-safe default rather than a stale value.
      setRet(prev => (prev && prev > new Date() ? prev : new Date(Date.now() + 14 * 86400000)));
    }
  };

  const submit = (e) => {
    e.preventDefault();
    let outboundLegs;
    if (tripType === 'multi') {
      outboundLegs = legs;
    } else if (tripType === 'return') {
      // Return = outbound + inbound as two canonical legs. This eliminates
      // stale `ret` state and guarantees the payload differs from One-way.
      outboundLegs = [legs[0], { from: legs[0].to, to: legs[0].from, depart: ret }];
    } else {
      outboundLegs = [legs[0]];
    }
    onSearch({
      type: 'flights',
      tripType,
      from: outboundLegs[0].from,
      to: outboundLegs[0].to,
      depart: outboundLegs[0].depart,
      ret: tripType === 'return' ? ret : null,
      legs: outboundLegs,
      pax,
      directOnly,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="flex flex-wrap items-center gap-4">
        <RadioGroup value={tripType} onValueChange={changeTripType} className="flex items-center gap-4">
          {['return','oneway','multi'].map(v => (
            <div key={v} className="flex items-center gap-2">
              <RadioGroupItem value={v} id={`tt-${v}`} data-testid={`trip-type-${v}`} />
              <Label htmlFor={`tt-${v}`} className="text-sm font-medium capitalize cursor-pointer">
                {v === 'return' ? 'Return' : v === 'oneway' ? 'One-way' : 'Multi-city'}
              </Label>
            </div>
          ))}
        </RadioGroup>
        <label className="flex items-center gap-2 text-sm ml-auto cursor-pointer">
          <Checkbox checked={directOnly} onCheckedChange={setDirectOnly} data-testid="direct-only-checkbox" /> Direct flights only
        </label>
      </div>

      {tripType !== 'multi' ? (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-3 bg-white border border-slate-200 rounded-xl p-3 hover:border-teal-500 transition-colors relative">
            <AirportPicker value={legs[0].from} onChange={(v) => updateLeg(0, { from: v })} placeholder="From" icon={Plane} testId="from-airport-picker-0" />
          </div>
          <div className="md:col-span-3 bg-white border border-slate-200 rounded-xl p-3 hover:border-teal-500 transition-colors relative">
            <AirportPicker value={legs[0].to} onChange={(v) => updateLeg(0, { to: v })} placeholder="To" icon={MapPin} testId="to-airport-picker-0" />
            <button type="button" onClick={() => swapLeg(0)} className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white border border-slate-200 items-center justify-center hover:border-teal-500 hover:text-teal-700 shadow-sm" aria-label="Swap" data-testid="swap-airports-0">
              <ArrowLeftRight className="w-4 h-4" />
            </button>
          </div>
          <div className="md:col-span-2 bg-white border border-slate-200 rounded-xl p-3 hover:border-teal-500 transition-colors">
            <DateField label="Depart" date={legs[0].depart} onSelect={(d) => updateLeg(0, { depart: d })} testId="depart-date-picker-0" />
          </div>
          <div className={cn("md:col-span-2 bg-white border border-slate-200 rounded-xl p-3 hover:border-teal-500 transition-colors", tripType !== 'return' && 'opacity-50 pointer-events-none')} data-testid="return-date-container">
            <DateField label="Return" date={ret} onSelect={setRet} minDate={legs[0].depart} testId="return-date-picker" />
          </div>
          <div className="md:col-span-2 bg-white border border-slate-200 rounded-xl p-3 hover:border-teal-500 transition-colors">
            <PassengerPicker value={pax} onChange={setPax} />
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="multi-city-legs">
          {legs.map((leg, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-stretch" data-testid={`multi-leg-row-${i}`}>
              <div className="md:col-span-1 flex items-center">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Flight {i + 1}</span>
              </div>
              <div className="md:col-span-3 bg-white border border-slate-200 rounded-xl p-3 hover:border-teal-500 transition-colors relative">
                <AirportPicker value={leg.from} onChange={(v) => updateLeg(i, { from: v })} placeholder="From" icon={Plane} testId={`from-airport-picker-${i}`} />
              </div>
              <div className="md:col-span-3 bg-white border border-slate-200 rounded-xl p-3 hover:border-teal-500 transition-colors relative">
                <AirportPicker value={leg.to} onChange={(v) => updateLeg(i, { to: v })} placeholder="To" icon={MapPin} testId={`to-airport-picker-${i}`} />
                <button type="button" onClick={() => swapLeg(i)} className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white border border-slate-200 items-center justify-center hover:border-teal-500 hover:text-teal-700 shadow-sm" aria-label="Swap" data-testid={`swap-airports-${i}`}>
                  <ArrowLeftRight className="w-4 h-4" />
                </button>
              </div>
              <div className="md:col-span-3 bg-white border border-slate-200 rounded-xl p-3 hover:border-teal-500 transition-colors">
                <DateField label="Depart" date={leg.depart} onSelect={(d) => updateLeg(i, { depart: d })} minDate={i > 0 ? legs[i - 1].depart : undefined} testId={`depart-date-picker-${i}`} />
              </div>
              <div className="md:col-span-2 flex items-center justify-end">
                {legs.length > 2 && (
                  <button type="button" onClick={() => removeLeg(i)} className="text-xs font-semibold text-slate-500 hover:text-red-600 underline underline-offset-2" data-testid={`remove-flight-button-${i}`}>
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-8 flex items-center">
              <button
                type="button"
                onClick={addLeg}
                disabled={legs.length >= MAX_LEGS}
                className="text-sm font-semibold text-teal-700 hover:text-teal-800 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                data-testid="add-flight-button"
              >
                + Add another flight {legs.length >= MAX_LEGS ? `(max ${MAX_LEGS})` : ''}
              </button>
            </div>
            <div className="md:col-span-4 bg-white border border-slate-200 rounded-xl p-3 hover:border-teal-500 transition-colors">
              <PassengerPicker value={pax} onChange={setPax} />
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button type="submit" size="lg" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold px-8 h-12 rounded-full" data-testid="search-flights-button">
          <Search className="w-5 h-5 mr-2" /> Search flights
        </Button>
      </div>
    </form>
  );
};

const HotelForm = ({ onSearch, initial }) => {
  const [dest, setDest] = useState(initial?.dest || '');
  const [checkIn, setCheckIn] = useState(initial?.checkIn ? new Date(initial.checkIn) : new Date(Date.now() + 7*86400000));
  const [checkOut, setCheckOut] = useState(initial?.checkOut ? new Date(initial.checkOut) : new Date(Date.now() + 10*86400000));
  const [rooms, setRooms] = useState(initial?.rooms || 1);
  const [guests, setGuests] = useState(initial?.guests || 2);

  const submit = (e) => { e.preventDefault(); onSearch({ type: 'hotels', dest, checkIn, checkOut, rooms, guests }); };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-12 gap-3">
      <div className="md:col-span-5 bg-white border border-slate-200 rounded-xl p-3 hover:border-teal-500 transition-colors">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Destination or hotel</div>
        <div className="flex items-center gap-2 mt-1">
          <Building2 className="w-4 h-4 text-teal-700" />
          <input value={dest} onChange={(e) => setDest(e.target.value)} placeholder="e.g. Bangkok, Marina Bay" className="flex-1 outline-none font-display font-bold text-slate-900 placeholder:text-slate-400 placeholder:font-medium bg-transparent" data-testid="hotel-destination-input" />
        </div>
        <div className="text-xs text-slate-500">Search cities, regions or hotels</div>
      </div>
      <div className="md:col-span-2 bg-white border border-slate-200 rounded-xl p-3 hover:border-teal-500 transition-colors">
        <DateField label="Check-in" date={checkIn} onSelect={setCheckIn} testId="hotel-checkin-picker" />
      </div>
      <div className="md:col-span-2 bg-white border border-slate-200 rounded-xl p-3 hover:border-teal-500 transition-colors">
        <DateField label="Check-out" date={checkOut} onSelect={setCheckOut} minDate={checkIn} testId="hotel-checkout-picker" />
      </div>
      <div className="md:col-span-3 bg-white border border-slate-200 rounded-xl p-3 hover:border-teal-500 transition-colors">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Rooms & guests</div>
        <div className="flex items-center gap-3 mt-2">
          <Select value={String(rooms)} onValueChange={(v) => setRooms(Number(v))}>
            <SelectTrigger className="h-9" data-testid="hotel-rooms-trigger"><SelectValue placeholder="Rooms" /></SelectTrigger>
            <SelectContent>{[1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)}>{n} Room{n>1?'s':''}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(guests)} onValueChange={(v) => setGuests(Number(v))}>
            <SelectTrigger className="h-9" data-testid="hotel-guests-trigger"><SelectValue placeholder="Guests" /></SelectTrigger>
            <SelectContent>{[1,2,3,4,5,6,7,8].map(n => <SelectItem key={n} value={String(n)}>{n} Guest{n>1?'s':''}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="md:col-span-12 flex justify-end">
        <Button type="submit" size="lg" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold px-8 h-12 rounded-full" data-testid="search-hotels-button">
          <Search className="w-5 h-5 mr-2" /> Search hotels
        </Button>
      </div>
    </form>
  );
};

const CarForm = ({ onSearch, initial }) => {
  const [location, setLocation] = useState(initial?.location || '');
  const [pickUp, setPickUp] = useState(initial?.pickUp ? new Date(initial.pickUp) : new Date(Date.now() + 3*86400000));
  const [dropOff, setDropOff] = useState(initial?.dropOff ? new Date(initial.dropOff) : new Date(Date.now() + 6*86400000));
  const [driverAge, setDriverAge] = useState(initial?.driverAge || '30-69');
  const submit = (e) => { e.preventDefault(); onSearch({ type: 'cars', location, pickUp, dropOff, driverAge }); };
  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-12 gap-3">
      <div className="md:col-span-4 bg-white border border-slate-200 rounded-xl p-3 hover:border-teal-500 transition-colors">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Pick-up location</div>
        <div className="flex items-center gap-2 mt-1">
          <Car className="w-4 h-4 text-teal-700" />
          <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="City or airport" className="flex-1 outline-none font-display font-bold text-slate-900 placeholder:text-slate-400 placeholder:font-medium bg-transparent" data-testid="car-location-input" />
        </div>
        <div className="text-xs text-slate-500">Airports, cities, hotels</div>
      </div>
      <div className="md:col-span-3 bg-white border border-slate-200 rounded-xl p-3 hover:border-teal-500 transition-colors">
        <DateField label="Pick-up date" date={pickUp} onSelect={setPickUp} testId="car-pickup-picker" />
      </div>
      <div className="md:col-span-3 bg-white border border-slate-200 rounded-xl p-3 hover:border-teal-500 transition-colors">
        <DateField label="Drop-off date" date={dropOff} onSelect={setDropOff} minDate={pickUp} testId="car-dropoff-picker" />
      </div>
      <div className="md:col-span-2 bg-white border border-slate-200 rounded-xl p-3 hover:border-teal-500 transition-colors">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Driver age</div>
        <Select value={driverAge} onValueChange={setDriverAge}>
          <SelectTrigger className="mt-1 border-0 p-0 h-auto font-display font-bold text-slate-900 focus:ring-0" data-testid="car-driverage-trigger"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="21-24">21–24</SelectItem>
            <SelectItem value="25-29">25–29</SelectItem>
            <SelectItem value="30-69">30–69</SelectItem>
            <SelectItem value="70+">70+</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="md:col-span-12 flex justify-end">
        <Button type="submit" size="lg" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold px-8 h-12 rounded-full" data-testid="search-cars-button">
          <Search className="w-5 h-5 mr-2" /> Search cars
        </Button>
      </div>
    </form>
  );
};

export default function SearchWidget({ defaultTab = 'flights', compact = false, initial = null }) {
  const navigate = useNavigate();

  // --- Travelpayouts flightSearch builder --------------------------------
  // While Travelpayouts wl_id=22421 is Cheapora's active flight-results
  // engine, Cheapora's SearchWidget owns the customer-facing controls and
  // serialises its state into the official Aviasales `flightSearch` URL
  // grammar so the WL renders matching results in #tpwl-tickets.
  //
  // Grammar (as observed live on cheapora.com and per Aviasales docs):
  //   SEG1[SEG2...] + PAX_DIGITS + [CABIN_LETTER]
  //
  //   SEG (full)     = ORIG_IATA(3) + DDMM(4) + DEST_IATA(3)
  //   Return trip    = SEG + DDMM_return (return DEST is implied = ORIG)
  //   One-way        = single SEG
  //   Multi-city     = concatenated full SEGs
  //   PAX            = adults [ + children [ + infants ] ] as digits
  //   CABIN (lower)  = w = Premium Economy / Comfort
  //                    c = Business
  //                    f = First
  //                    (Economy → cabin letter omitted)
  const cabinToCode = (cabinLabel) => {
    const c = String(cabinLabel || '').toLowerCase();
    if (c.startsWith('premium') || c === 'comfort') return 'w';
    if (c.startsWith('business')) return 'c';
    if (c === 'first') return 'f';
    return '';
  };
  const twoDigit = (n) => String(n).padStart(2, '0');
  const toDDMM = (d) => {
    if (!d) return '';
    const dt = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return twoDigit(dt.getDate()) + twoDigit(dt.getMonth() + 1);
  };
  const IATA = (s) => String(s || '').trim().toUpperCase().slice(0, 3);

  const buildFlightSearch = (payload) => {
    const cabinCode = cabinToCode(payload?.pax?.cabin);
    const adults = Math.max(1, Number(payload?.pax?.adults) || 1);
    const children = Math.max(0, Number(payload?.pax?.children) || 0);
    const infants = Math.max(0, Number(payload?.pax?.infants) || 0);
    const pax = (infants > 0)
      ? `${adults}${children}${infants}`
      : (children > 0)
        ? `${adults}${children}`
        : `${adults}`;

    let segs = '';
    const tripType = payload?.tripType || 'return';

    if (tripType === 'multi' && Array.isArray(payload.legs) && payload.legs.length >= 2) {
      for (const leg of payload.legs) {
        const o = IATA(leg.from);
        const d = IATA(leg.to);
        const dd = toDDMM(leg.depart);
        if (!o || !d || !dd || o.length !== 3 || d.length !== 3) return null;
        segs += `${o}${dd}${d}`;
      }
    } else {
      const o = IATA(payload.from);
      const d = IATA(payload.to);
      const dd = toDDMM(payload.depart);
      if (!o || !d || !dd || o.length !== 3 || d.length !== 3) return null;
      segs = `${o}${dd}${d}`;
      if (tripType === 'return') {
        const rd = toDDMM(payload.ret);
        if (!rd) return null;
        segs += rd;
      }
    }
    return `${segs}${pax}${cabinCode}`;
  };

  const handleSearch = (payload) => {
    // Serialise Dates so the payload is safe to store in both React Router
    // location.state (structured-clone) and sessionStorage.
    const serialised = {
      ...payload,
      depart: payload.depart?.toISOString?.() || payload.depart || null,
      ret: payload.ret?.toISOString?.() || payload.ret || null,
      checkIn: payload.checkIn?.toISOString?.() || payload.checkIn || null,
      checkOut: payload.checkOut?.toISOString?.() || payload.checkOut || null,
      pickUp: payload.pickUp?.toISOString?.() || payload.pickUp || null,
      dropOff: payload.dropOff?.toISOString?.() || payload.dropOff || null,
      legs: Array.isArray(payload.legs)
        ? payload.legs.map(l => ({
            from: l.from,
            to: l.to,
            depart: l.depart?.toISOString?.() || l.depart || null,
          }))
        : undefined,
    };

    if (payload.type === 'flights') {
      // Empirically verified on production 2026-09-22 that driving the WL
      // via `?flightSearch=` triggers the tp-em.com/entrypoint_config CORS
      // block and the WL surfaces its "connection has disappeared" modal.
      // Until Travelpayouts unblocks that config fetch (or provisions a
      // results-only wl_id), keep the historic state-based navigation so
      // Cheapora's own compact widget on /flights still receives the
      // user's input. `buildFlightSearch` remains in place for immediate
      // re-enable once TP resolves the CORS restriction.
      try { sessionStorage.setItem('waan_last_search', JSON.stringify(serialised)); } catch(_) {}
      const state = { search: serialised, searchTs: Date.now() };
      navigate('/flights', { state });
      return;
    }
    if (payload.type === 'hotels') {
      // Hotels: URL query string is the canonical source of truth.
      const qs = new URLSearchParams({
        dest: payload.dest || '',
        checkIn: (serialised.checkIn || '').slice(0, 10),
        checkOut: (serialised.checkOut || '').slice(0, 10),
        rooms: String(payload.rooms ?? 1),
        guests: String(payload.guests ?? 2),
      });
      navigate(`/hotels?${qs.toString()}`);
      return;
    }
    if (payload.type === 'cars') {
      const qs = new URLSearchParams({
        location: payload.location || '',
        pickUp: (serialised.pickUp || '').slice(0, 16),
        dropOff: (serialised.dropOff || '').slice(0, 16),
        age: payload.driverAge || '30-69',
      });
      navigate(`/cars?${qs.toString()}`);
      return;
    }
  };
  return (
    <div className={cn("w-full bg-white shadow-2xl rounded-2xl p-4 md:p-6 border border-slate-100", compact && 'shadow-md')}>
      <Tabs defaultValue={defaultTab}>
        <TabsList className="bg-slate-100 rounded-full p-1">
          <TabsTrigger value="flights" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-teal-700 data-[state=active]:shadow px-4 md:px-6 gap-2"><Plane className="w-4 h-4" /> Flights</TabsTrigger>
          <TabsTrigger value="hotels" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-teal-700 data-[state=active]:shadow px-4 md:px-6 gap-2"><Building2 className="w-4 h-4" /> Hotels</TabsTrigger>
          <TabsTrigger value="cars" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-teal-700 data-[state=active]:shadow px-4 md:px-6 gap-2"><Car className="w-4 h-4" /> Cars</TabsTrigger>
        </TabsList>
        <TabsContent value="flights" className="mt-5"><FlightForm onSearch={handleSearch} initial={initial} /></TabsContent>
        <TabsContent value="hotels" className="mt-5"><HotelForm onSearch={handleSearch} initial={initial} /></TabsContent>
        <TabsContent value="cars" className="mt-5"><CarForm onSearch={handleSearch} initial={initial} /></TabsContent>
      </Tabs>
    </div>
  );
}
